import * as React from 'react';

import { ENG_COUNTRIES } from '@nexxt/common/demographics/types';
import { Project } from '@nexxt/common/types';

import * as countryList from 'react-select-country-list';
import Select from 'react-select';

interface CountryDropdownProps {
    editEnabled: boolean;
    useIncaPanel: boolean;
    currentProject: Project;
    onCountryChange: (countries: { value: string; label: string }[]) => void;
}

const CountryDropdown = ({
    editEnabled,
    useIncaPanel,
    currentProject,
    onCountryChange
}: CountryDropdownProps): JSX.Element => {
    const selfProvidedCountryList = countryList().getData();
    const incaPanelSupportedCountryList = selfProvidedCountryList.filter(
        (country) => ENG_COUNTRIES.includes(country.value)
    );
    const [selectedCountries, setSelectedCountries] = React.useState<
        { value: string; label: string }[]
    >([]);
    const [newCountries, setNewCountries] = React.useState<
        { value: string; label: string }[]
    >([...selfProvidedCountryList]);
    const [isFocus, setIsFocus] = React.useState<boolean>(false);

    React.useEffect(() => {
        !useIncaPanel
            ? setSelectedCountries(currentProject.countries)
            : setSelectedCountries([]);
    }, [useIncaPanel]);
    const groupedOptions = [
        {
            label: 'Selected Countries',
            options: selectedCountries
        },
        {
            label: 'Countries',
            options: newCountries
        }
    ];
    const multiValueContainer = ({ selectProps, data }): string => {
        const last =
            data.label ===
            selectProps.value[selectProps.value.length - 1].label;
        const str = `${data.label}${!last ? ',' : ' '} `;
        return isFocus ? '' : str;
    };

    const handleMultiSelectedCountries = (
        response: {
            value: string;
            label: string;
        }[]
    ): void => {
        if (response.length > 0) {
            const selectedCountry = [...response].pop();
            const updatedCountries = [...newCountries].filter(
                (country) => country.value !== selectedCountry.value
            );
            setSelectedCountries(response);
            setNewCountries(updatedCountries);
            onCountryChange(response);
        } else {
            setSelectedCountries([]);
            setNewCountries(selfProvidedCountryList);
            onCountryChange([]);
        }
    };
    const singleDropdownStyles = {
        control: (base) => ({
            ...base,
            height: 35,
            minHeight: 35,
            background: '#F2F2F2',
            borderColor:
                currentProject.countries?.length === 0
                    ? 'red'
                    : base.borderColor
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            textOverflow: 'ellipsis',
            maxWidth: '90%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            display: 'initial'
        })
    };
    const multiDropdownStyles = {
        control: (base) => ({
            ...base,
            height: 35,
            minHeight: 35,
            background: '#F2F2F2'
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            flexWrap: 'nowrap'
        })
    };
    return useIncaPanel ? (
        <Select
            options={incaPanelSupportedCountryList}
            styles={singleDropdownStyles}
            value={currentProject.countries}
            onChange={(response: { value: string; label: string }) =>
                onCountryChange([response])
            }
            components={{
                IndicatorSeparator: () => null,
                ClearIndicator: () => null
            }}
            isDisabled={!editEnabled}
        />
    ) : (
        <Select
            styles={multiDropdownStyles}
            options={groupedOptions}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            isSearchable={true}
            isMulti
            value={selectedCountries}
            components={{
                MultiValueContainer: multiValueContainer,
                IndicatorSeparator: () => null,
                ClearIndicator: () => null
            }}
            onChange={(response: { value: string; label: string }[]) => {
                handleMultiSelectedCountries(response);
            }}
            onFocus={() => setIsFocus(true)}
            onBlur={() => setIsFocus(false)}
            isDisabled={!editEnabled}
        />
    );
};

export default CountryDropdown;
